import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const defaultForm = {
  market_type: 'crypto',
  symbol: '',
  exchange: '',
  direction: 'long',
  status: 'closed',
  entry_price: '',
  exit_price: '',
  quantity: '',
  fees: '',
  entry_date: '',
  exit_date: '',
  strategy: '',
  notes: '',
}

const exchanges = ['Binance', 'Bybit', 'Other']

export default function TradeForm({ onClose, onSaved, existingTrade }) {
  const { user } = useAuth()
  const [form, setForm] = useState(existingTrade || defaultForm)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const calculatePnl = () => {
    const entry = parseFloat(form.entry_price)
    const exit = parseFloat(form.exit_price)
    const qty = parseFloat(form.quantity)
    const fees = parseFloat(form.fees) || 0
    if (!entry || !exit || !qty) return { pnl: null, pnl_percentage: null }
    const raw = form.direction === 'long' ? (exit - entry) * qty : (entry - exit) * qty
    const pnl = raw - fees
    const pnl_percentage = ((pnl / (entry * qty)) * 100)
    return { pnl: parseFloat(pnl.toFixed(4)), pnl_percentage: parseFloat(pnl_percentage.toFixed(2)) }
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    const { pnl, pnl_percentage } = calculatePnl()

    const payload = {
      ...form,
      user_id: user.id,
      entry_price: parseFloat(form.entry_price),
      exit_price: form.exit_price ? parseFloat(form.exit_price) : null,
      quantity: parseFloat(form.quantity),
      fees: parseFloat(form.fees) || 0,
      pnl,
      pnl_percentage,
    }

    let error
    if (existingTrade) {
      ({ error } = await supabase.from('trades').update(payload).eq('id', existingTrade.id))
    } else {
      ({ error } = await supabase.from('trades').insert(payload))
    }

    if (error) setError(error.message)
    else { onSaved(); onClose() }
    setLoading(false)
  }

  const inputClass = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
  const labelClass = "text-xs text-gray-400 mb-1 block"

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-white font-bold text-lg">{existingTrade ? 'Edit Trade' : 'Log New Trade'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          
          {/* Market Type */}
          <div>
            <label className={labelClass}>Market Type</label>
            <select value={form.market_type} onChange={e => set('market_type', e.target.value)} className={inputClass}>
              <option value="crypto">Crypto</option>
              <option value="forex">Forex</option>
            </select>
          </div>

          {/* Exchange */}
          <div>
            <label className={labelClass}>Exchange / Broker</label>
            <select value={form.exchange} onChange={e => set('exchange', e.target.value)} className={inputClass}>
              <option value="">Select...</option>
              {exchanges.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>

          {/* Symbol */}
          <div>
            <label className={labelClass}>Symbol</label>
            <input value={form.symbol} onChange={e => set('symbol', e.target.value.toUpperCase())} className={inputClass} placeholder="e.g. BTCUSDT" />
          </div>

          {/* Direction */}
          <div>
            <label className={labelClass}>Direction</label>
            <div className="flex gap-2">
              {['long', 'short'].map(d => (
                <button key={d} onClick={() => set('direction', d)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                    form.direction === d
                      ? d === 'long' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Entry Price */}
          <div>
            <label className={labelClass}>Entry Price</label>
            <input type="number" value={form.entry_price} onChange={e => set('entry_price', e.target.value)} className={inputClass} placeholder="0.00" />
          </div>

          {/* Exit Price */}
          <div>
            <label className={labelClass}>Exit Price</label>
            <input type="number" value={form.exit_price} onChange={e => set('exit_price', e.target.value)} className={inputClass} placeholder="0.00" />
          </div>

          {/* Quantity */}
          <div>
            <label className={labelClass}>Quantity</label>
            <input type="number" value={form.quantity} onChange={e => set('quantity', e.target.value)} className={inputClass} placeholder="0.00" />
          </div>

          {/* Fees */}
          <div>
            <label className={labelClass}>Fees</label>
            <input type="number" value={form.fees} onChange={e => set('fees', e.target.value)} className={inputClass} placeholder="0.00" />
          </div>

          {/* Entry Date */}
          <div>
            <label className={labelClass}>Entry Date</label>
            <input type="datetime-local" value={form.entry_date} onChange={e => set('entry_date', e.target.value)} className={inputClass} />
          </div>

          {/* Exit Date */}
          <div>
            <label className={labelClass}>Exit Date</label>
            <input type="datetime-local" value={form.exit_date} onChange={e => set('exit_date', e.target.value)} className={inputClass} />
          </div>

          {/* Status */}
          <div>
            <label className={labelClass}>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)} className={inputClass}>
              <option value="closed">Closed</option>
              <option value="open">Open</option>
            </select>
          </div>

          {/* Strategy */}
          <div>
            <label className={labelClass}>Strategy (optional)</label>
            <input value={form.strategy} onChange={e => set('strategy', e.target.value)} className={inputClass} placeholder="e.g. Breakout" />
          </div>

          {/* Notes */}
          <div className="col-span-2">
            <label className={labelClass}>Notes (optional)</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} className={inputClass} rows={3} placeholder="What did you observe?" />
          </div>
        </div>

        {error && <p className="mt-4 text-red-400 text-sm">{error}</p>}

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-gray-800 text-gray-400 hover:text-white text-sm transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading} className="flex-1 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium transition-colors">
            {loading ? 'Saving...' : existingTrade ? 'Update Trade' : 'Save Trade'}
          </button>
        </div>
      </div>
    </div>
  )
}